import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Stack,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro/DateRangeCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimeRangePicker } from "@mui/x-date-pickers-pro/DateTimeRangePicker";
import { I18n } from "@aws-amplify/core";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import { get } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";
import "dayjs/locale/it";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/de";
import {
  LocalizationProvider,
  MobileDateTimePicker,
  TimeField,
} from "@mui/x-date-pickers";
import AccountListPopup from "../../../../popup/AccountListPopup";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
dayjs.extend(localizedFormat);
dayjs.extend(utc);

const AlarmsFilter = ({
  alarmFilterSwitches,
  setAlarmFilterSwitches,
  alarmFilterDateRange,
  setAlarmFilterDateRange,
  setSelectedCodes,
  selectedCodes,
  setOpen,
  dateRange,
  setDateRange,
  open,
  language,
  setIsEffectRunning,
  fetchLists,
  isAutoRefresh,
  filterDuraion,
  setFilterDuration,
  accountSelected,
  setAccountSelected,
}) => {
  const [duration, setDuration] = useState(24);
  const [isDateRange, setIsDateRange] = useState(false);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [startDateCustom, setStartDateCustom] = useState();
  const [endDateCustom, setEndDateCustom] = useState();
  const [error, setError] = useState(false);
  const [account_number, setAccountNumber] = useState("");
  const [isAccountListOpen, setIsAccountListOpen] = useState(false);
  const [accountList, setAccountList] = useState([]);
  const [errorAccount, setErrorAccount] = useState(false);

  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      setIsDateRange(!dayjs(dateRange[0]).isSame(dateRange[1], "day"));
    }
  }, [dateRange]);

  useEffect(() => {
    dayjs.locale(language || "en");
  }, [language]);

  useEffect(() => {
    setShowCustomDate(false);
    setDuration("");
    // setAccountNumber("");
    //setAccountSelected("");
    setIsAccountListOpen(false);
    if(dateRange && dateRange[0] && dateRange[1]) {
      setStartDateCustom(dayjs(dateRange[0]).tz().format("YYYY-MM-DD HH:mm"));
      setEndDateCustom(dayjs(dateRange[0]).tz().format("YYYY-MM-DD HH:mm"));
    }else {
      setStartDateCustom(dayjs().tz().format("YYYY-MM-DD HH:mm"));
      setEndDateCustom(dayjs().tz().format("YYYY-MM-DD HH:mm"));
    }
  },[open])

  useEffect(() => {
    setShowCustomDate(false);
    setDuration("");
    setStartDateCustom(dayjs(dateRange[0]).tz().format("YYYY-MM-DD 00:00:00"));
    setEndDateCustom(dayjs(dateRange[0]).tz().format("YYYY-MM-DD 01:00:00"));
  }, [dateRange]);

  // This effect is used to check if the end date is after the start date
  useEffect(() => {
    if(endDateCustom < startDateCustom) {
      setError(true);
    } else {
      setError(false);
    }
  }, [startDateCustom, endDateCustom]);

  const handleSwitchChange = (id) => (event) => {
    setSelectedCodes((prev) =>
      event.target.checked ? [...prev, id] : prev.filter((c) => c !== id)
    );
  };

  const stateOptions = [
    { id: 0, label: I18n.get("Inactive") },
    { id: 1, label: I18n.get("Active") },
    { id: 2, label: I18n.get("Closed") },
    { id: 3, label: I18n.get("Suspended") },
    { id: 4, label: I18n.get("Suspended With Lock") },
  ];

  const durationOptions = [
    { id: 1, label: I18n.get("1 hour") },
    { id: 3, label: I18n.get("3 hours") },
    { id: 6, label: I18n.get("6 hours") },
    { id: 12, label: I18n.get("12 hours") },
    { id: 24, label: I18n.get("24 hours") },
    { id: "custom", label: I18n.get("Select Date") },
  ];

  const isDateRangePickerEnabled =
    selectedCodes.includes(0) || selectedCodes.includes(2);

  const handleDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") {
      return;
    }
    setOpen(false);
  };

  const handleDurationChange = (selectedValue) => {
    setDuration(selectedValue);
    setShowCustomDate(selectedValue === "custom");
  };

  const handleSubmit = async () => {
    let [startDate] = dateRange || [];
    setAccountSelected(account_number);
    if (isDateRange) {
      if (!startDate || !dateRange[1]) return;
      const startDateFormatted = dayjs(dateRange[0]).format(
        "YYYY-MM-DD 00:00:00"
      );
      const endDateFormatted = dayjs(dateRange[1]).format(
        "YYYY-MM-DD 23:59:59"
      );
      setIsEffectRunning(true);
      setAlarmFilterDateRange([startDateFormatted, endDateFormatted]);
      setAlarmFilterSwitches(selectedCodes);
      setOpen(false);
    } else {
      if (!startDate) return;
      if(duration === "custom") {
        const startDateFormatted = dayjs(startDateCustom).utc().format("YYYY-MM-DD HH:mm:ss");
        const endDateFormatted = dayjs(endDateCustom).utc().format("YYYY-MM-DD HH:mm:ss");

        setIsEffectRunning(false);
        setFilterDuration(duration);
        setAlarmFilterDateRange([startDateFormatted, endDateFormatted]);
        setAlarmFilterSwitches(selectedCodes);
        fetchLists([startDateFormatted, endDateFormatted], selectedCodes, account_number);
        setOpen(false);
      } else {
        const now = dayjs();
        const selectedStartDate = dayjs(startDate)
          .hour(now.hour())
          .minute(now.minute())
          .second(now.second());
        const calculatedStartDate = selectedStartDate.subtract(duration, "hour");
        let startDatePreFromatted = "";
        let endDatePreFromatted = "";

        // If the duration is empty or null, set the start and end dates to the same day
        if(duration === "" || duration === null ) {
          startDatePreFromatted = startDate.tz().format("YYYY-MM-DD 00:00:00");
          endDatePreFromatted = startDate.tz().format("YYYY-MM-DD 23:59:59");
        } else {
          startDatePreFromatted = calculatedStartDate.utc().format("YYYY-MM-DD HH:mm:ss");
          endDatePreFromatted = selectedStartDate.utc().format("YYYY-MM-DD HH:mm:ss");
        }
        const startDateFormatted = startDatePreFromatted;
        const endDateFormatted = endDatePreFromatted;

        setIsEffectRunning(false);
        setFilterDuration(duration);
        setAlarmFilterDateRange([startDateFormatted, endDateFormatted]);
        setAlarmFilterSwitches(selectedCodes);
        fetchLists([startDateFormatted, endDateFormatted], selectedCodes, account_number);
        setOpen(false);
      }
    }
  };

  const isActiveButton = useMemo(() => {
    return (
      selectedCodes.includes(1) ||
      selectedCodes.includes(3) ||
      selectedCodes.includes(4)
    );
  }, [selectedCodes]);
  const isInactiveButton = useMemo(() => {
    return selectedCodes.includes(0) || selectedCodes.includes(2);
  }, [selectedCodes]);

  const isRangeGreaterThan72 = 
    dateRange &&
    dateRange[0] &&
    dateRange[1] &&
    dayjs(dateRange[1]).diff(dayjs(dateRange[0]), "day", true) > 3;
    
  const isRangeGreaterThan72Custom = 
    startDateCustom &&
    endDateCustom &&
    dayjs(endDateCustom).diff(dayjs(startDateCustom), "day", true) > 3;

  let isButtonDisabled = false;
  if (selectedCodes.length === 0) {
    isButtonDisabled = true;
  } else {
    if (isDateRangePickerEnabled) {
      //calendario abilitato
      if (!isDateRange && duration === "custom") {
        if (isRangeGreaterThan72Custom) {
          isButtonDisabled = true;
        } else {
          isButtonDisabled = false;
        }
      }else {
        if (isRangeGreaterThan72) {
          isButtonDisabled = true;
        } else {
          isButtonDisabled = false;
        }
      }
    } else {//calendario disabilitato, non mi interessa il range
      isButtonDisabled = false;
    }
  }

  const handleSaveAccountNumber = (formData) => {
    setAccountNumber(formData.account_number);
    setIsAccountListOpen(false);
  }

  const fetchAccountNumbers = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/accountNumber/get",
        options: {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${await currentUserToken()}`,
        },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      setAccountList(response);
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  useEffect(() => {
    fetchAccountNumbers();
  },[]);

  useEffect(() => {
    if(account_number !== "") {
      if( accountList.find(account => account.account_number === account_number)) {
        setErrorAccount(false);
      } else {
        setErrorAccount(true);
      }
    }else{
      setErrorAccount(false);
    }
  },[account_number]);

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
    >
      <DialogContent sx={{ pt: 0, pb: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack spacing={2} alignItems="center">
            <Typography variant="h5" sx={{ pb: 2 }}>
              {I18n.get("Filters")}
            </Typography>
            <Divider component="div" orientation="horizontal" flexItem />
            {stateOptions.map(({ id, label }) => (
              <FormControlLabel
                key={id}
                control={
                  <Switch
                    checked={selectedCodes.includes(id)}
                    onChange={handleSwitchChange(id)}
                  />
                }
                label={label}
                labelPlacement="bottom"
              />
            ))}
          </Stack>
          <Divider
            component="div"
            orientation="vertical"
            flexItem
            sx={{ height: "500px" }}
          />
          <Stack spacing={1} alignItems="center">
            <Typography variant="h5" sx={{ pt: 2 }}>
              {I18n.get("Time Range")}
            </Typography>
            { account_number === "" ?
              <Typography variant = "caption">
                {I18n.get("You can select up to 3 days.")}
              </Typography>
              : ""
            }
            
            <Divider component="div" orientation="horizontal" flexItem />
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={language || "en"}
            >
              {/* Change the calendar to */}
              {/* <DateTimeRangePicker 
                value={dateRange}
                onChange={(newValue) => setDateRange(newValue)}
                disabled={!isDateRangePickerEnabled}
                views={['day', 'hours', 'minutes']}
                timePicker='true'  
                /> */}
              <DateRangeCalendar
                value={dateRange}
                onChange={(newValue) => setDateRange(newValue)}
                disabled={!isDateRangePickerEnabled}
              />
            </LocalizationProvider>
            <FormControl margin="dense" fullWidth>
              <InputLabel>{I18n.get("Duration")}</InputLabel>
              <Select
                label="Duration"
                value={isDateRange ? "" : duration}
                onChange={(e) => handleDurationChange(e.target.value)}
                fullWidth
                disabled={isDateRange || !isDateRangePickerEnabled}
              >
                {durationOptions.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {showCustomDate && !isDateRange && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  display="flex"
                  //justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  gap={2} // Add gap to increase space between the two TextFields
                >
                <TimeField 
                  value={isDateRange ? "" : dayjs(startDateCustom)}
                  label={I18n.get("Start Date")}
                  error={error}
                  onChange={(newDate) => {
                    const formattedDate = dayjs(newDate).format(
                      "YYYY-MM-DD HH:mm:ss"
                    );
                    setStartDateCustom(formattedDate);
                  }}
                  format="HH:mm"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        style: {
                          padding: "4px",
                          fontSize: "14px",
                        },
                      }}
                    />
                    
                  )}
                />
                <TimeField 
                  value={isDateRange ? "" : dayjs(endDateCustom)}
                  label={I18n.get("End Date")}
                  error={error}
                  onChange={(newDate) => {
                    const formattedDate = dayjs(newDate).format(
                      "YYYY-MM-DD HH:mm:ss"
                    );
                    setEndDateCustom(formattedDate);
                  }}
                  format="HH:mm"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        style: {
                          padding: "4px",
                          fontSize: "14px",
                        },
                      }}
                    />
                    
                  )}
                />
                </Box>
                <Box
                  display="flex"
                  //justifyContent="space-between"
                  alignItems="left"
                  width="100%"
                  gap={2} // Add gap to increase space between the two TextFields
                >
                { error && (
                  <Typography variant="caption" color="error">
                    {I18n.get("The end date must be after the start date")}
                  </Typography>
                )}
                </Box>
              </LocalizationProvider>
            )}
            <Box display="flex" flexDirection="row"  gap={2} alignContent={"center"} alignItems={"center"} width="100%">
              <FormControl fullWidth margin="small" className="custom-form-control">
              <TextField
                label={I18n.get("Account Number")}
                value={account_number || ""}
                onChange={(e) => {
                  setAccountNumber(e.target.value);
                }}
                fullWidth
                margin="normal"
                name="asset"
                error={errorAccount}
                helperText={errorAccount ? I18n.get("Account number not found") : ""}
                className="custom-text-field"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SettingsSuggestIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => setIsAccountListOpen(true)}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <FormHelperText>{I18n.get("Search for an account number or select from the list")}
              </FormHelperText>
              </FormControl>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} style={{color: (isButtonDisabled || error || errorAccount || account_number !== "") ? "red" : "gray"}}>
          {I18n.get("Close")}</Button>
        <Button disabled={(isButtonDisabled && account_number === "") || error || errorAccount} onClick={handleSubmit}>
          {I18n.get("Submit")}
        </Button>
      </DialogActions>
      {isAccountListOpen && (
        <AccountListPopup
          open={isAccountListOpen}
          onClose={() => setIsAccountListOpen(false)}
          step={0}
          handleSaveAccountNumber={handleSaveAccountNumber}
        />
      )}
    </Dialog>
  );
};
export default AlarmsFilter;
